import {
	IEvFiltersPanelDefinition,
	IFilterDefinition,
	ISelectFilterDefinition,
	OptionType,
	TEvFiltersPanelValues
} from '@evinced-private/ui-common';

import { IFilterOptions } from './DataFilterHelper';

type TPanelComponentProps = IEvFiltersPanelDefinition['componentProps'];
type TPanelValue = string | OptionType[];

export interface IAppliedFilterChip {
	filterId: string;
	filterLabel: string;
	valueLabel: string;
}

const areSelectValuesEqual = (
	current: OptionType[] | undefined,
	defaultValue: OptionType[] | undefined
): boolean => {
	if (!current && !defaultValue) {
		return true;
	}
	if (!current || !defaultValue) {
		return false;
	}
	if (current.length !== defaultValue.length) {
		return false;
	}

	const currentValues = current.map((option) => option.value).sort();
	const defaultValues = defaultValue.map((option) => option.value).sort();

	return currentValues.every((value, index) => value === defaultValues[index]);
};

/**
 * A panel value is considered "empty" when it carries no active selection:
 * nullish, an empty string, or an empty array. Empty values must not become
 * chips and must not be persisted as table state, otherwise the panel renders
 * stale "key only" chips and empty filters linger in storage.
 */
const isEmptyPanelValue = (value: unknown): boolean =>
	value === undefined ||
	value === null ||
	value === '' ||
	(Array.isArray(value) && value.length === 0);

const getFriendlyLabel = (definition: IFilterDefinition): string => {
	const componentLabel = (definition.componentProps as { label?: unknown } | undefined)?.label;
	return typeof componentLabel === 'string' && componentLabel ? componentLabel : definition.label;
};

/**
 * Extends the legacy `EvFilters` `IFilterDefinition` with the optional
 * `isPinned` flag understood by `EvFiltersPanel`. Pinned filters are excluded
 * from the applied-filter chip count and rendered as a separate editable chip
 * (e.g. the default test period shown after "Filtered by: N filters applied").
 */
interface IPinnableFilterDefinition extends IFilterDefinition {
	isPinned?: boolean;
}

export type IPinnableSelectFilterDefinition = ISelectFilterDefinition & {
	isPinned?: boolean;
};

/**
 * Adapts the legacy `EvFilters` `IFilterDefinition[]` shape to the
 * `EvFiltersPanel` `IEvFiltersPanelDefinition[]` shape so existing filter
 * definition files can be reused without rewriting each one.
 *
 * `chipLabel` is derived from the friendly `componentProps.label` (e.g.
 * "Severity") rather than the internal top-level `label` (e.g. "severities"),
 * so applied-filter chips show a human readable name instead of the filter key.
 */
export const mapToPanelDefinitions = (
	definitions: IPinnableFilterDefinition[] = []
): IEvFiltersPanelDefinition[] =>
	definitions.map((definition) => ({
		id: definition.id,
		filterType: definition.filterType,
		label: definition.label,
		srTitle: definition.srTitle,
		chipLabel: getFriendlyLabel(definition),
		isPinned: definition.isPinned,
		defaultValue: (definition.defaultValue ??
			(definition.filterType === 'text' ? '' : [])) as TPanelValue,
		componentProps: definition.componentProps as TPanelComponentProps
	}));

/**
 * Converts the flat internal `IFilterOptions` table state into the flat
 * `TEvFiltersPanelValues` shape expected by `EvFiltersPanel`, dropping any
 * undefined/null entries.
 */
const mapFiltersToPanelValues = (filters: IFilterOptions = {}): TEvFiltersPanelValues =>
	Object.entries(filters).reduce<TEvFiltersPanelValues>((acc, [key, value]) => {
		if (value === undefined || value === null) {
			return acc;
		}
		acc[key] = value as TPanelValue;
		return acc;
	}, {});

/**
 * Builds the `initialValues` for `EvFiltersPanel`.
 *
 * Takes the already-mapped `IEvFiltersPanelDefinition[]` (consumers memoize this
 * via {@link mapToPanelDefinitions}) so the definitions are not mapped twice per
 * render.
 *
 * The panel uses the provided `initialValues` object as-is and does NOT
 * backfill missing definition keys with their defaults. Any definition key
 * absent from this object is read as `undefined`, which the panel treats as
 * "different from default" and renders as an empty chip showing only the
 * filter key. To avoid that, we seed every definition with its default value
 * and overlay only the non-empty values coming from the current table state.
 */
export const buildPanelInitialValues = (
	panelDefinitions: IEvFiltersPanelDefinition[] = [],
	filters: IFilterOptions = {}
): TEvFiltersPanelValues => {
	const seeded = panelDefinitions.reduce<TEvFiltersPanelValues>((acc, definition) => {
		acc[definition.id] = definition.defaultValue;
		return acc;
	}, {});

	return Object.entries(mapFiltersToPanelValues(filters)).reduce<TEvFiltersPanelValues>(
		(acc, [key, value]) => {
			if (!isEmptyPanelValue(value)) {
				acc[key] = value;
			}
			return acc;
		},
		seeded
	);
};

/**
 * Converts the flat `TEvFiltersPanelValues` returned by `EvFiltersPanel`
 * back into the internal `IFilterOptions` table state. Empty values are
 * stripped so cleared/untouched filters are not persisted to storage.
 */
export const mapPanelValuesToFilters = <T extends IFilterOptions = IFilterOptions>(
	values: TEvFiltersPanelValues = {}
): T =>
	Object.entries(values).reduce<T>(
		(acc, [key, value]) => (isEmptyPanelValue(value) ? acc : { ...acc, [key]: value }),
		{} as T
	);

/**
 * Builds the non-pinned applied-filter chips shown in `EvFiltersPanelBar`,
 * mirroring ui-common `generateChips` so overflow UI can list the same filters.
 */
export const generateAppliedFilterChips = (
	values: TEvFiltersPanelValues = {},
	panelDefinitions: IEvFiltersPanelDefinition[] = []
): IAppliedFilterChip[] => {
	const chips: IAppliedFilterChip[] = [];

	for (const {
		id,
		filterType,
		defaultValue,
		chipLabel: definitionChipLabel,
		label,
		isPinned
	} of panelDefinitions) {
		if (isPinned) {
			continue;
		}

		const currentValue = values[id] ?? defaultValue;
		const chipLabel = definitionChipLabel || label;

		if (filterType === 'text') {
			if (currentValue !== defaultValue && currentValue !== '') {
				chips.push({
					filterId: id,
					filterLabel: chipLabel,
					valueLabel: currentValue as string
				});
			}
			continue;
		}

		if (!areSelectValuesEqual(currentValue as OptionType[], defaultValue as OptionType[])) {
			const valueLabel = (currentValue as OptionType[] | undefined)
				?.map((option) => option.label)
				.join(', ');

			chips.push({
				filterId: id,
				filterLabel: chipLabel,
				valueLabel: valueLabel || ''
			});
		}
	}

	return chips;
};

export const resetPanelFilterToDefault = (
	values: TEvFiltersPanelValues,
	filterId: string,
	panelDefinitions: IEvFiltersPanelDefinition[]
): TEvFiltersPanelValues => {
	const definition = panelDefinitions.find((item) => item.id === filterId);

	if (!definition) {
		return values;
	}

	return {
		...values,
		[filterId]: definition.defaultValue
	};
};
